import { ItemFragmentFragment, type Item } from '@apps/www/src/__generated__/graphql';
import SVGridItemHoverOverlayContainer from '@apps/www/src/www/containers/SVGridItemHoverOverlayContainer';
import useAllowedFeature from '@apps/www/src/www/hooks/useAllowedFeature';
import useAuthSettings from '@apps/www/src/www/hooks/useAuthSettings';
import useGridPageRole from '@apps/www/src/www/hooks/useGridPageRole';
import useIsLoggedIn from '@apps/www/src/www/hooks/useIsLoggedIn';
import { UIStateKeys, useSetUIState } from '@apps/www/src/www/hooks/useUIState';
import useUserAndBoardPageQuery from '@apps/www/src/www/hooks/useUserAndBoardPageQuery';
import { type RootState } from '@apps/www/src/www/reducers';
import { clearItemsSelection, toggleItemSelection } from '@apps/www/src/www/reducers/grid';
import { UPLOAD_DONE } from '@apps/www/src/www/reducers/gridUpload';
import SVA from '@pkgs/shared-client/components/SVA';
import SVBottomGridUpsell from '@pkgs/shared-client/components/SVBottomGridUpsell';
import SVButton, { SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import type {
	ItemPassthroughProps,
	LikeItem,
	Props as SVGridProps,
} from '@pkgs/shared-client/components/SVGrid';
import SVGrid from '@pkgs/shared-client/components/SVGrid';
import SVGridCardItem from '@pkgs/shared-client/components/SVGridCardItem';
import SVGridCards from '@pkgs/shared-client/components/SVGridCards';
import SVGridItem from '@pkgs/shared-client/components/SVGridItem';
import SVLink from '@pkgs/shared-client/components/SVLink';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import { GridHoverOverlayUISize } from '@pkgs/shared/constants';
import AllowedFeature from '@pkgs/shared/enums/AllowedFeature';
import BoardUserRole from '@pkgs/shared/enums/BoardUserRole';
import FeedSourceType from '@pkgs/shared/enums/FeedSourceType';
import GridLayoutMode from '@pkgs/shared/enums/GridLayoutMode';
import ItemsSortMethod from '@pkgs/shared/enums/ItemsSortMethod';
import boardUserRoleHasBoardUserRolePrivileges from '@pkgs/shared/helpers/boardUserRoleHasBoardUserRolePrivileges';
import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUnmount } from 'react-use';
import SVGridSortingContainer from './SVGridSortingContainer';

const _BottomLogin = () => {
	return (
		<div className="flex justify-center py-24">
			<div className="max-w-xl flex-col justify-center space-y-6">
				<h3 className="type-subtitle font-semibold tracking-tight">
					Join to see more saves
				</h3>
				<div className="flex-center flex space-x-6">
					<SVButton
						use={SVButtonUSES.PRIMARY}
						Component={SVLink}
						to="/join/"
						title="Join"
					>
						Join
					</SVButton>
					<SVA Component={SVLink} to="/login/" title="Log in">
						Log in
					</SVA>
				</div>
			</div>
		</div>
	);
};

type Props = Pick<
	SVGridProps,
	| 'columnsConfigID'
	| 'spacingConfigID'
	| 'sourceType'
	| 'items'
	| 'isLoading'
	| 'loadingSkeletonCount'
	| 'onPaginate'
	| 'isStatic'
	| 'onItemClick'
> & {
	sortMethod?: ValueOf<typeof ItemsSortMethod>;
	layoutMode?: ValueOf<typeof GridLayoutMode>;
	isLimited?: boolean;
};

function isItemFragment(item: LikeItem): item is ItemFragmentFragment {
	return '__typename' in item && item.__typename === 'Item' && !('upload' in item);
}

// Detached into a component so `renderHoverOverlay` can re-render when `item` changes and we can
// avoid having complicated logic inside `SVGridItem` to decide if re-rendering is needed.
function _GridItem({
	item,
	router,
	canSort,
	isSelected,
	sortingItemID,
	isSortLoading,
	role,
	sourceType,
	...props
}: {
	item: LikeItem;
	sourceType?: ValueOf<typeof FeedSourceType>;
	router: NextRouter;
	canSort: boolean;
	isSelected: boolean;
	sortingItemID: Item['_id'] | null;
	isSortLoading?: boolean;
} & ItemPassthroughProps) {
	const renderHoverOverlay = useCallback(
		(props: { uiSize: GridHoverOverlayUISize; canMove: boolean }) => {
			return isItemFragment(item) ? (
				<SVGridItemHoverOverlayContainer
					item={item}
					isSortLoading={isSortLoading || false}
					{...props}
				/>
			) : null;
		},
		[item, isSortLoading],
	);

	return (
		<SVGridItem
			{...SVGridItem.itemToProps(item)}
			sourceType={sourceType}
			routerPathname={router.pathname}
			routerQuery={router.query}
			canSort={canSort}
			isSelected={isSelected}
			isSorting={Boolean(sortingItemID && sortingItemID === item._id)}
			renderHoverOverlay={renderHoverOverlay}
			role={role}
			{...props}
		/>
	);
}

const SVBaseItemsContainer = ({
	columnsConfigID,
	items,
	isLoading,
	loadingSkeletonCount,
	onPaginate,
	sourceType,
	spacingConfigID,
	sortMethod,
	isStatic,
	layoutMode = GridLayoutMode.GRID,
	onItemClick,
	isLimited,
}: Props) => {
	// When grid is from related items, we strip out any editing capability or communication with the
	// global state of current page items
	const isRelatedItems = sourceType === FeedSourceType.RELATED;

	const router = useRouter();
	const isLoggedIn = useIsLoggedIn();
	const authSettings = useAuthSettings(['autoPlayGIFs']);
	const { user, board } = useUserAndBoardPageQuery();
	const setPageGridItems = useSetUIState(UIStateKeys.CURRENT_PAGE_GRID_ITEMS);
	const hasAutoPlayGIF = useAllowedFeature(AllowedFeature.AUTO_PLAY_GIF);
	const dispatch = useDispatch();

	// For related items, we treat the role as viewer only
	const originalPageRole = useGridPageRole();
	const role = isRelatedItems ? BoardUserRole.VIEWER : originalPageRole;

	const { isSorting, isSortLoading, isEditing, uploadingItems, selectedItemIDs, sortingItemID } =
		useSelector((state: RootState) => ({
			isSorting: state.grid.isSorting,
			isSortLoading: state.grid.isSortLoading,
			isEditing: state.grid.isEditing,
			uploadingItems: state.gridUpload.items,
			selectedItemIDs: state.grid.selectedItemIDs,
			sortingItemID: state.grid.sortingItemID,
		}));

	const handleItemClick = useEventCallback((itemID: Item['_id'], event: React.UIEvent) => {
		if (onItemClick) {
			onItemClick(itemID, event);
		}

		if (isEditing) {
			dispatch(toggleItemSelection(itemID));

			// Prevent default behavior when editing to avoid navigating to item detail page
			event.preventDefault();
			return false;
		}
	});

	// Needs to be a regular useCallback to change reference every time selectedItemIDs updates, thus re-rendering the grid
	const isItemSelected = useCallback(
		(itemID: Item['_id']) => {
			return selectedItemIDs?.includes(itemID);
		},
		[selectedItemIDs],
	);

	useEffect(() => {
		if (!isRelatedItems) {
			setPageGridItems(items || []);
		}
	}, [setPageGridItems, items, isRelatedItems]);

	useUnmount(() => {
		if (!isRelatedItems) {
			setPageGridItems([]);
			dispatch(clearItemsSelection());
		}
	});

	const autoPlayGIFs = Boolean(hasAutoPlayGIF && authSettings?.autoPlayGIFs);
	const itemsOrEmpty = items || [];
	const hasEditorPrivileges = boardUserRoleHasBoardUserRolePrivileges(role, BoardUserRole.EDITOR);

	let allItems = itemsOrEmpty;

	// Dedupe items based on item.asset._id. We shoulnd't have duplicates on
	// the grid, but sometimes the server returns duplicates and it needs to be
	// fixed there. This is just a temporary fail safe.
	allItems = allItems.filter(
		(item, index, self) => index === self.findIndex((t) => t.asset._id === item.asset._id),
	);

	if (hasEditorPrivileges && uploadingItems?.length) {
		allItems = [
			...uploadingItems.filter(
				(item) =>
					item.upload.status != UPLOAD_DONE &&
					(!board || item.upload.boardID === board._id),
			),
			...allItems,
		];
	}

	const canSort = hasEditorPrivileges && sortMethod === ItemsSortMethod.CUSTOM;

	const showBottomLogin = isLimited && !isLoggedIn && !isLoading;
	const showBottomUpsell = isLimited && !showBottomLogin && !isLoading;

	const commonGridProps = {
		autoPlayGIFs,
		// columnsConfigID can be undefined if viewportName is not ready yet (on server and first client render)
		columnsConfigID,
		isLoaded: !isLoading && Boolean(items),
		isLoading: isLoading || !columnsConfigID,
		loadingSkeletonCount,
		role,
		sourceType,
		isSorting,
		isSortLoading,
		isStatic,
		items: allItems,
		keyExtractor: (item: LikeItem) => item._id,
		onItemClick: handleItemClick,
		onPaginate: showBottomUpsell ? undefined : onPaginate, // Don't paginate if we're showing the upsell at the end
		spacingConfigID,
	};

	// Render grid layout
	const renderGridLayout = () => (
		<SVGrid
			{...commonGridProps}
			renderItem={(item, props) => (
				<_GridItem
					item={item}
					router={router}
					canSort={canSort}
					isSelected={isItemSelected(item._id)}
					sortingItemID={sortingItemID}
					{...props}
				/>
			)}
		/>
	);

	// Render card layout
	const renderCardLayout = () => (
		<SVGridCards
			{...commonGridProps}
			renderItem={(item, height) => (
				<SVGridCardItem
					item={item}
					isSelected={isItemSelected(item._id)}
					autoPlayGIFs={autoPlayGIFs}
					routerPathname={router.pathname}
					routerQuery={router.query}
					url={item.url}
					height={height}
					renderHoverOverlay={(overlayProps) => {
						return isItemFragment(item) ? (
							<SVGridItemHoverOverlayContainer
								item={item}
								isSortLoading={isSortLoading || false}
								{...overlayProps}
							/>
						) : null;
					}}
				/>
			)}
		/>
	);

	return (
		<div>
			{layoutMode === GridLayoutMode.GRID && renderGridLayout()}
			{layoutMode === GridLayoutMode.CARD && renderCardLayout()}
			{canSort && user && (
				<SVGridSortingContainer
					items={itemsOrEmpty}
					board={board}
					user={user}
					sortMethod={sortMethod}
				/>
			)}
			{showBottomLogin ? <_BottomLogin /> : null}
			{showBottomUpsell ? <SVBottomGridUpsell /> : null}
		</div>
	);
};

export default SVBaseItemsContainer;
